import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { DataGridPro } from "@mui/x-data-grid-pro";
import {
  Backdrop,
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import {
  addDays,
  addMonths,
  addYears,
  isAfter,
  isBefore,
  format,
} from "date-fns";
import {
  useDispatch,
  adminActions,
  useSelector,
  adminSelectors,
  authSelectors,
  uiSelectors,
} from "../../state";
import { LEADS_COLUMNS } from "../../constants/tables";
import { formatTitleCase } from "../../utils";
import {
  NotesIcon,
  RestartAltIcon,
  SwapHorizIcon,
} from "../../components/icons";
import { useResponsiveDataGridProps } from "../../hooks/useResponsiveDataGridProps";
import type { GridColDef } from "@mui/x-data-grid-pro";
import reliExchangeLoader from "../../assets/reliExchange-loader.gif";

const LEAD_STATUSES = ["active", "contacted", "closed_won", "closed_lost"];

const modalStyle = {
  position: "fixed" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  width: { xs: "calc(100vw - 32px)", sm: 500 },
  maxWidth: 600,
  borderRadius: 1,
  zIndex: 1,
};

function LeadNotesCell({ leadId }: { leadId: number }) {
  const dispatch = useDispatch();
  const leadNotes = useSelector(adminSelectors.leadNotes);
  const [open, setOpen] = useState(false);
  const [newNote, setNewNote] = useState("");

  const handleOpen = () => {
    setOpen(true);
    dispatch(adminActions.getLeadNotes(leadId));
  };

  const handleClose = () => {
    setOpen(false);
    setNewNote("");
    dispatch(adminActions.setLeadNotes([]));
  };

  const handleAddNote = () => {
    if (!newNote.trim()) return;
    dispatch(adminActions.addLeadNote(leadId, newNote.trim()));
    setNewNote("");
  };

  return (
    <>
      <IconButton
        size="small"
        title="Notes"
        color="primary"
        onClick={handleOpen}
      >
        <NotesIcon fontSize="small" />
      </IconButton>
      <Modal open={open} onClose={handleClose}>
        <Box sx={modalStyle}>
          <Typography variant="h6" mb={2}>
            Lead Notes
          </Typography>
          <Box sx={{ maxHeight: 350, overflowY: "auto", mb: 2 }}>
            {leadNotes.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No notes yet.
              </Typography>
            ) : (
              leadNotes.map((n, i) => (
                <Box key={n.id} sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {n.user
                      ? `${n.user.first_name} ${n.user.last_name}`
                      : "Unknown"}{" "}
                    &middot;{" "}
                    {n.created_at
                      ? format(new Date(n.created_at), "MM/dd/yyyy h:mm a")
                      : ""}
                  </Typography>
                  <Typography variant="body2">{n.note}</Typography>
                  {i < leadNotes.length - 1 && <Divider sx={{ mt: 1 }} />}
                </Box>
              ))
            )}
          </Box>
          <TextField
            label="Add a note"
            multiline
            minRows={2}
            fullWidth
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
          />
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}
          >
            <Button onClick={handleClose}>Close</Button>
            <Button
              variant="contained"
              onClick={handleAddNote}
              disabled={!newNote.trim()}
            >
              Add Note
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

function LeadReQuoteCell({ reqUid }: { reqUid: string }) {
  const dispatch = useDispatch();
  const isLoading = useSelector(uiSelectors.loading);
  const [isRequoting, setIsRequoting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (isRequoting && !isLoading) {
      setIsRequoting(false);
    }
  }, [isLoading, isRequoting]);

  const handleRequoteClick = () => {
    setShowConfirm(true);
  };

  const handleConfirm = () => {
    setShowConfirm(false);
    setIsRequoting(true);
    dispatch(adminActions.reQuoteLead(reqUid));
  };

  const handleCancel = () => {
    setShowConfirm(false);
  };

  const showBackdrop = isRequoting && isLoading;

  return (
    <>
      <IconButton
        size="small"
        title="Re-Quote"
        color="primary"
        onClick={handleRequoteClick}
        disabled={isRequoting}
      >
        <RestartAltIcon fontSize="small" />
      </IconButton>
      <Modal open={showConfirm} onClose={handleCancel}>
        <Box sx={modalStyle}>
          <Typography variant="h6" mb={2}>
            Confirm Client Authorization
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            I certify that, prior to requesting or obtaining any insurance
            score, I received the prospective client's express authorization to
            access and use their insurance score for the purpose of preparing
            and providing insurance quotes. I acknowledge that this
            authorization was obtained in accordance with all applicable federal
            and state laws and regulations governing the use of insurance scores
            and consumer information.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleConfirm}>
              Confirm
            </Button>
          </Box>
        </Box>
      </Modal>
      {showBackdrop &&
        ReactDOM.createPortal(
          <Backdrop
            open
            sx={{
              zIndex: 9999,
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              bgcolor: "transparent",
            }}
          >
            <Box sx={{ pointerEvents: "none" }}>
              <img
                src={reliExchangeLoader}
                style={{ height: "160px" }}
                alt="Loading"
              />
            </Box>
          </Backdrop>,
          document.body,
        )}
    </>
  );
}

function LeadTransferCell({ reqUid }: { reqUid: string }) {
  const dispatch = useDispatch();
  const agents = useSelector(adminSelectors.agents);
  const user = useSelector(authSelectors.user);
  const role = useSelector(authSelectors.activeRole);
  const agencyId = useSelector(authSelectors.activeAgencyId);
  const [open, setOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState("");

  const handleOpen = () => {
    setOpen(true);
    if (role && agencyId) {
      dispatch(
        adminActions.getAgents(
          role,
          user?.sales_director?.[0]?.agency_groups_id,
          agencyId,
        ),
      );
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedAgent("");
  };

  const handleTransfer = () => {
    if (!selectedAgent || !role || !agencyId) return;
    dispatch(adminActions.transferLead(reqUid, selectedAgent, role, agencyId));
    handleClose();
  };

  return (
    <>
      <IconButton
        size="small"
        title="Transfer"
        color="primary"
        onClick={handleOpen}
      >
        <SwapHorizIcon fontSize="small" />
      </IconButton>
      <Modal open={open} onClose={handleClose}>
        <Box sx={modalStyle}>
          <Typography variant="h6" mb={1}>
            Transfer Lead
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Assign this lead to another agent.
          </Typography>
          <TextField
            select
            variant="outlined"
            fullWidth
            label="Select an agent"
            value={selectedAgent}
            onChange={(e) => setSelectedAgent(e.target.value)}
            size="small"
          >
            {agents.map((a, i) => (
              <MenuItem key={i} value={a.referral_token}>
                {a.user.last_name} {a.user.first_name} - {a.agency.name}
              </MenuItem>
            ))}
          </TextField>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}
          >
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleTransfer}
              disabled={!selectedAgent}
            >
              Transfer
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

export default function LeadsPage() {
  const dispatch = useDispatch();
  const leads = useSelector(adminSelectors.leads);
  const agencyId = useSelector(authSelectors.activeAgencyId);
  const activeRole = useSelector(authSelectors.activeRole);
  const [pageSize, setPageSize] = useState(25);
  const [activeStatus, setActiveStatus] = useState<string>("");
  const [datePeriod, setDatePeriod] = useState<string>("");

  useEffect(() => {
    if (!activeRole || !agencyId) return;
    dispatch(adminActions.getLeads(activeRole, agencyId));
  }, [dispatch, activeRole, agencyId]);

  const getDateCutoff = () => {
    const now = new Date();
    switch (datePeriod) {
      case "30":
        return addDays(now, 30);
      case "60":
        return addDays(now, 60);
      case "180":
        return addMonths(now, 6);
      case "365":
        return addYears(now, 1);
      default:
        return null;
    }
  };

  const filteredLeads = leads.filter((l) => {
    if (activeStatus && l.lead_status !== activeStatus) return false;
    if (datePeriod) {
      const cutoff = getDateCutoff();
      if (!cutoff || !l.expiration_date) return false;
      const expDate = new Date(l.expiration_date);
      return isAfter(expDate, new Date()) && isBefore(expDate, cutoff);
    }
    return true;
  });

  const rows = filteredLeads.map((l) => ({
    ...l,
    agency: typeof l.agency === "string" ? JSON.parse(l.agency) : l.agency,
    agent: typeof l.agent === "string" ? JSON.parse(l.agent) : l.agent,
  }));

  const handleStatusChange = (leadId: number, newStatus: string) => {
    if (!activeRole || !agencyId) return;
    dispatch(
      adminActions.updateLeadStatusAction(
        leadId,
        newStatus,
        activeRole,
        agencyId,
      ),
    );
  };

  const columns = LEADS_COLUMNS.map((col) => {
    if (col.field === "lead_status") {
      return {
        ...col,
        renderCell: (params) => (
          <TextField
            select
            size="small"
            value={params.row.lead_status || ""}
            onChange={(e) => handleStatusChange(params.row.id, e.target.value)}
            variant="standard"
            fullWidth
            InputProps={{ disableUnderline: true }}
          >
            {LEAD_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {formatTitleCase(s.replace("_", " "))}
              </MenuItem>
            ))}
          </TextField>
        ),
      };
    }
    return col;
  });

  const transferColumn = {
    field: "transfer",
    headerName: "Transfer",
    width: 80,
    sortable: false,
    filterable: false,
    align: "center" as const,
    headerAlign: "center" as const,
    renderCell: (params) => <LeadTransferCell reqUid={params.row.req_uid} />,
  };

  const reQuoteColumn = {
    field: "requote",
    headerName: "Re-Quote",
    width: 90,
    sortable: false,
    filterable: false,
    align: "center" as const,
    headerAlign: "center" as const,
    renderCell: (params) => <LeadReQuoteCell reqUid={params.row.req_uid} />,
  };

  const notesColumn = {
    field: "notes",
    headerName: "Notes",
    width: 70,
    sortable: false,
    filterable: false,
    align: "center" as const,
    headerAlign: "center" as const,
    renderCell: (params) => <LeadNotesCell leadId={params.row.id} />,
  };

  const canTransfer =
    activeRole === "sales_admin" ||
    activeRole === "sales_director" ||
    activeRole === "super_admin";

  const viewColIndex = columns.findIndex((c) => c.field === "view");
  const extraColumns = canTransfer
    ? [notesColumn, transferColumn, reQuoteColumn]
    : [notesColumn, reQuoteColumn];
  const baseColumns =
    viewColIndex >= 0
      ? [
          ...columns.slice(0, viewColIndex),
          ...extraColumns,
          ...columns.slice(viewColIndex),
        ]
      : [...columns, ...extraColumns];

  // (6) Mobile-only "summary" column. It stacks the most useful fields into
  // one wide cell so a narrow phone screen still tells the user everything
  // they need. Hidden on desktop by `useResponsiveDataGridProps` when
  // `hasMobileSummaryColumn` is true.
  const mobileSummaryColumn: GridColDef = {
    field: "mobileSummary",
    headerName: "Lead",
    flex: 1,
    minWidth: 220,
    sortable: false,
    filterable: false,
    // (5) wrapText pairs with getRowHeight: 'auto' from the hook.
    cellClassName: "mobile-summary-cell",
    renderCell: (params) => {
      const row = params.row;
      const name = `${row.first_name || ""} ${row.last_name || ""}`.trim();
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.25,
            py: 1,
            width: "100%",
            minWidth: 0,
          }}
        >
          <Typography
            sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.2 }}
            noWrap
          >
            {name || "—"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: 1.3,
            }}
          >
            {row.email}
            {row.phone ? ` · ${row.phone}` : ""}
          </Typography>
          {(row.lead_status || row.expiration_date) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0.5,
                mt: 0.25,
              }}
            >
              {/* Inline status editor — mirrors the desktop `lead_status`
                  column override so phone/tablet users can change a
                  lead's status without scrolling horizontally to find
                  the dedicated status column. `stopPropagation` keeps
                  the dropdown open from triggering any future row-click
                  handler the page might wire up. */}
              <TextField
                select
                size="small"
                value={row.lead_status || ""}
                onChange={(e) => handleStatusChange(row.id, e.target.value)}
                onClick={(e) => e.stopPropagation()}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                SelectProps={{
                  sx: {
                    color: "#0033ff",
                    fontWeight: 500,
                    fontSize: 12,
                    "& .MuiSelect-select": {
                      padding: 0,
                      paddingRight: "18px !important",
                      minHeight: 0,
                    },
                    "& .MuiSelect-icon": {
                      right: 0,
                      fontSize: 18,
                      color: "#0033ff",
                    },
                  },
                }}
              >
                {LEAD_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {formatTitleCase(s.replace("_", " "))}
                  </MenuItem>
                ))}
              </TextField>
              {row.expiration_date && (
                <Typography
                  variant="caption"
                  sx={{ color: "#0033ff", fontWeight: 500 }}
                >
                  {row.lead_status ? "· " : ""}exp{" "}
                  {format(new Date(row.expiration_date), "MM/dd/yyyy")}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      );
    },
  };

  const allColumns: GridColDef[] = [mobileSummaryColumn, ...baseColumns];

  const responsiveProps = useResponsiveDataGridProps({
    // (2) Pin the identifier when scrolling horizontally on mobile.
    pinFirst: "mobileSummary",
    // (6) Toggles `mobileSummary` between mobile (visible) and desktop (hidden).
    hasMobileSummaryColumn: true,
    // Hide the per-field columns on mobile in favour of the summary cell.
    hideOnMobile: [
      "email",
      "phone",
      "customerName",
      "agentName",
      "agencyName",
      "expiration_date",
      "lead_status",
    ],
  });

  return (
    <div>
      <Typography variant="h1" sx={{ fontSize: 20, fontWeight: 600, my: 1 }}>
        Leads
      </Typography>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <TextField
          select
          label="Status"
          size="small"
          variant="outlined"
          value={activeStatus}
          onChange={(e) => setActiveStatus(e.target.value)}
          sx={{ width: 150 }}
        >
          <MenuItem value="">All</MenuItem>
          {LEAD_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {formatTitleCase(s.replace("_", " "))}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Expiring"
          size="small"
          variant="outlined"
          value={datePeriod}
          onChange={(e) => setDatePeriod(e.target.value)}
          sx={{ width: 150 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="30">Next 30 Days</MenuItem>
          <MenuItem value="60">Next 60 Days</MenuItem>
          <MenuItem value="180">Next 6 Months</MenuItem>
          <MenuItem value="365">Next Year</MenuItem>
        </TextField>
      </div>
      <DataGridPro
        autoHeight
        rows={rows}
        columns={allColumns}
        rowsPerPageOptions={[10, 25, 50, 100, 250]}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        pagination
        {...responsiveProps}
      />
    </div>
  );
}
